/**
 * Adapter: turn workspace store data (lights + IES grids, illuminated layers +
 * norms, extracted geometry) into the engine's `CalcInput`. Pure + testable;
 * the React/three side (scene geometry extraction) lives in the
 * `useIlluminatedGeometry` hook and feeds `LayerSource.geometry` here.
 *
 * See docs/frontend-measurement-calculation.md §6.
 */
import type { ExpectedValue, TargetFunction, TargetType } from '../../store/reducers/workspace.reducer';
import type { LampWireframeMapEntry } from '../../store/selectors/workspace.selector';
import type { IesCandelaGrid, LampWireframeThirdPartyEntry } from '../../types/lampWireframes';
import type { Light } from '../../types/light';
import type { Norm } from '../../types/user';
import type { CalcConfig, CalcInput, CalcLayer, CalcLuminaire, MeasurementField, SurfaceGeometry } from './types';

const PHOTOMETRIC_FIELDS: readonly MeasurementField[] = ['L', 'E', 'Ev', 'Esc', 'Ehs'];

/** Norm targets → `ExpectedValue[]` (same shape; just narrows string unions). */
export const normToExpectedValues = (norm: Norm): ExpectedValue[] =>
  norm.targets.map((t) => ({
    function: t.function as TargetFunction,
    type: t.type as TargetType,
    value: t.value,
  }));

/** Which photometric quantities a layer needs, derived from its norm targets. */
export const fieldsFromTargets = (targets: ExpectedValue[]): MeasurementField[] =>
  PHOTOMETRIC_FIELDS.filter((f) => targets.some((t) => t.function === f));

export interface LuminaireSource {
  light: Light;
  /** Candela grid for this light's IES (from `lampWireframes`); omit if unavailable. */
  iesGrid?: IesCandelaGrid;
}

/**
 * Aim rotation for photometry. During a market-match preview the placement
 * rotation lives on the third-party wireframe entry, not on workspace lamps.
 */
export const resolveCalcRotationRadians = (
  lampUuid: string,
  lightRotation: number | undefined,
  wireframeMap: Map<string, LampWireframeMapEntry>,
  thirdPartyWireframes: readonly LampWireframeThirdPartyEntry[] | null | undefined,
): number | undefined => {
  const preview = thirdPartyWireframes?.find((entry) => entry.lampUuid === lampUuid);
  if (!preview) return lightRotation;
  const fromMap = wireframeMap.get(lampUuid)?.wireframe?.rotation;
  if (fromMap != null) return fromMap;
  return preview.rotationRadians ?? lightRotation;
};

/** Build `CalcLuminaire[]`, skipping lights without a usable candela grid. */
export const buildCalcLuminaires = (sources: LuminaireSource[]): CalcLuminaire[] =>
  sources
    .filter((s): s is LuminaireSource & { iesGrid: IesCandelaGrid } => !!s.iesGrid && s.iesGrid.candela.length > 0)
    .map(
      (s): CalcLuminaire => ({
        uuid: s.light.uuid,
        position: [s.light.positionX, s.light.positionY, s.light.positionZ],
        height: s.light.distanceToGround,
        // Z-axis rotation (radians) from the in-scene rotation gizmo. The engine
        // offsets the IES azimuth by this value so the photometric distribution
        // points in the direction the user aimed it.
        rotation: s.light.rotationRadians != null ? [0, 0, s.light.rotationRadians] : undefined,
        candela: s.iesGrid,
      }),
    );

export interface LayerSource {
  uuid: string;
  /** Layer originalName (matches `MeasurementData.name`). */
  name: string;
  geometry: SurfaceGeometry;
  /** The layer's norm, if it has one. ml computes every layer regardless. */
  norm?: Norm;
  /** Individual targets, used instead of `norm` when the layer is not on a catalog class. */
  targets?: ExpectedValue[];
}

/**
 * Build `CalcLayer[]` for ALL layers (ml computes E/Ev/Esc for every mesh, not
 * just the normed ones). `fields` is the global set of quantities to compute
 * (union of all norms' target functions); a layer without a norm has empty
 * `targets` but still gets `fields` evaluated.
 */
export const buildCalcLayers = (sources: LayerSource[], fields: MeasurementField[]): CalcLayer[] =>
  sources.map(
    (s): CalcLayer => ({
      uuid: s.uuid,
      name: s.name,
      normId: s.norm?.id ?? 0,
      geometry: s.geometry,
      targets: s.targets?.length ? s.targets : s.norm ? normToExpectedValues(s.norm) : [],
      fields,
    }),
  );

export const buildCalcInput = (luminaires: CalcLuminaire[], layers: CalcLayer[], config: CalcConfig): CalcInput => ({
  luminaires,
  layers,
  config,
});
